import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, NavLink } from 'react-router-dom';
import { Avatar, Button, IconButton, makeStyles, Menu, MenuItem, Typography } from '@popmenu/common-ui';
import { useEatSession } from '../../hooks/useEatSession';
import { useRefinePath } from '../../hooks/useRefinePath';
import { useSignInPath } from '../../hooks/useSignInPath';
import { useSignOut } from '../../hooks/useSignOut';

const useStyles = makeStyles(theme => ({
  avatar: {
    backgroundColor: theme.palette.red.main,
    color: theme.palette.common.white,
    fontSize: '1rem',
    height: theme.spacing(4),
    width: theme.spacing(4),
  },
  // Keeps the signed-in Nav the same height as the signed-out one
  avatarButton: {
    padding: theme.spacing(0.5),
  },
  name: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(1, 2),
  },
}));

// Desktop account control; the mobile menu in Nav carries the same entries
export const NavAccountMenu = () => {
  const classes = useStyles();
  const intl = useIntl();
  const { loading, user } = useEatSession();
  const signInPath = useSignInPath();
  const signOut = useSignOut();
  const refinePath = useRefinePath();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  if (loading) return null;

  if (!user) {
    return (
      <Button component={NavLink} role="link" to={signInPath} variant="text">
        <FormattedMessage defaultMessage="Sign in" />
      </Button>
    );
  }

  const onSignOut = () => {
    setAnchorEl(null);
    void signOut();
  };

  return (
    <React.Fragment>
      <IconButton
        aria-controls="account-menu"
        aria-haspopup="true"
        aria-label={intl.formatMessage({ defaultMessage: 'Account menu for {name}' }, { name: user.displayName })}
        className={classes.avatarButton}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget)}
      >
        <Avatar className={classes.avatar}>{user.displayName.charAt(0).toUpperCase()}</Avatar>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        // Opens below the avatar instead of over it
        getContentAnchorEl={null}
        id="account-menu"
        keepMounted
        onClose={() => setAnchorEl(null)}
        open={Boolean(anchorEl)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        <Typography className={classes.name} variant="body2">{user.displayName}</Typography>
        <MenuItem component={Link} onClick={() => setAnchorEl(null)} to="/profile">
          <FormattedMessage defaultMessage="Your taste profile" />
        </MenuItem>
        {refinePath && (
          <MenuItem component={Link} onClick={() => setAnchorEl(null)} to={refinePath}>
            <FormattedMessage defaultMessage="Refine your palate" />
          </MenuItem>
        )}
        <MenuItem onClick={onSignOut}>
          <FormattedMessage defaultMessage="Sign out" />
        </MenuItem>
      </Menu>
    </React.Fragment>
  );
};
